import { useState, useEffect } from 'react'
import { useAppSelector } from '../hooks/redux'
import { Project, User } from '@/types'
import {
  PencilIcon,
  UserPlusIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  FolderIcon,
  UsersIcon,
  DocumentTextIcon,
  EyeIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { Dialog, Transition, Menu } from '@headlessui/react'
import { Fragment } from 'react'
import * as adminApi from '../services/api/admin'

interface ProjectWithCounts extends Project {
  _count: {
    issues: number
    members: number
  }
}

const ProjectManagement = () => {
  const { user } = useAppSelector(state => state.auth)
  const [projects, setProjects] = useState<ProjectWithCounts[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [editingProject, setEditingProject] = useState<ProjectWithCounts | null>(null)
  const [addingMembers, setAddingMembers] = useState<ProjectWithCounts | null>(null)
  const [creatingProject, setCreatingProject] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [uploadingIcon, setUploadingIcon] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // 表單狀態
  const [editForm, setEditForm] = useState({
    name: '',
    key: '',
    description: '',
    icon: '',
    archived: false,
    allowPublicView: false,
  })

  // 建立專案表單狀態
  const [createForm, setCreateForm] = useState({
    name: '',
    key: '',
    description: '',
    icon: '',
    allowPublicView: false,
  })

  // 載入專案和使用者資料
  useEffect(() => {
    loadProjects()
    loadUsers()
  }, [includeArchived])

  const loadProjects = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getAllProjects(includeArchived)
      const nextProjects = (response.data?.projects ?? []) as ProjectWithCounts[]
      setProjects(nextProjects)
    } catch (error) {
      console.error('載入專案失敗:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      const response = await adminApi.getAllUsers()
      setUsers(response.data?.users ?? [])
    } catch (error) {
      console.error('載入使用者失敗:', error)
    }
  }

  // 開啟建立專案對話框
  const openCreateProject = () => {
    setCreatingProject(true)
    setCreateForm({
      name: '',
      key: '',
      description: '',
      icon: '',
      allowPublicView: false,
    })
  }

  // 開啟編輯專案對話框
  const openEditProject = (project: ProjectWithCounts) => {
    setEditingProject(project)
    setEditForm({
      name: project.name,
      key: project.key,
      description: project.description || '',
      icon: project.icon || '',
      archived: project.archived,
      allowPublicView: (project as any).allowPublicView || false,
    })
  }

  // 建立專案
  const handleCreateProject = async () => {
    try {
      const payload = {
        name: createForm.name.trim(),
        key: createForm.key.trim(),
        allowPublicView: createForm.allowPublicView,
        ...(createForm.description.trim() ? { description: createForm.description.trim() } : {}),
        ...(createForm.icon.trim() ? { icon: createForm.icon.trim() } : { icon: null }),
      }

      await adminApi.createProject(payload)
      setCreatingProject(false)
      loadProjects()
      alert('專案建立成功')
    } catch (error) {
      console.error('建立專案失敗:', error)
      alert('建立失敗')
    }
  }

  // 更新專案
  const handleUpdateProject = async () => {
    if (!editingProject) return

    try {
      const payload = {
        name: editForm.name.trim(),
        key: editForm.key.trim(),
        description: editForm.description.trim() || undefined,
        icon: editForm.icon.trim() ? editForm.icon.trim() : null,
        archived: editForm.archived,
        allowPublicView: editForm.allowPublicView,
      }

      await adminApi.updateProject(editingProject.id, payload)
      setEditingProject(null)
      loadProjects()
    } catch (error) {
      console.error('更新專案失敗:', error)
      alert('更新失敗')
    }
  }

  // 切換封存狀態
  const toggleArchiveProject = async (project: ProjectWithCounts) => {
    try {
      await adminApi.updateProject(project.id, { archived: !project.archived })
      loadProjects()
    } catch (error) {
      console.error('切換封存狀態失敗:', error)
      alert('操作失敗')
    }
  }

  // 開啟成員管理對話框
  const openAddMembers = (project: ProjectWithCounts) => {
    setAddingMembers(project)
    setSelectedUsers([])
    setSearchTerm('')
  }

  // 新增成員到專案
  const handleAddMembers = async () => {
    if (!addingMembers || selectedUsers.length === 0) return

    try {
      const response = await adminApi.addProjectMembers(addingMembers.id, selectedUsers)
      setSelectedUsers([])
      // 重新載入專案並更新對話框中的成員列表
      await loadProjects()
      // 更新對話框中顯示的專案資料
      const updatedProject = projects.find(p => p.id === addingMembers.id)
      if (updatedProject) {
        setAddingMembers(updatedProject)
      }
      const addedCount = response.data?.addedCount ?? selectedUsers.length
      alert(`成功添加 ${addedCount} 位成員`)
    } catch (error) {
      console.error('添加成員失敗:', error)
      alert('添加失敗')
    }
  }

  // 移除成員（從成員管理對話框中使用）
  const removeMemberFromDialog = async (userId: string, userName: string) => {
    if (!addingMembers || !confirm(`確定要從專案中移除 ${userName} 嗎？`)) return

    try {
      await adminApi.removeProjectMember(addingMembers.id, userId)
      // 重新載入專案並更新對話框中的成員列表
      await loadProjects()
      // 更新對話框中顯示的專案資料
      const updatedProject = projects.find(p => p.id === addingMembers.id)
      if (updatedProject) {
        setAddingMembers(updatedProject)
      }
    } catch (error) {
      console.error('移除成員失敗:', error)
      alert('移除失敗')
    }
  }

  // 移除成員（從專案卡片中使用）
  const removeMember = async (projectId: string, userId: string, userName: string) => {
    if (!confirm(`確定要從專案中移除 ${userName} 嗎？`)) return

    try {
      await adminApi.removeProjectMember(projectId, userId)
      loadProjects()
    } catch (error) {
      console.error('移除成員失敗:', error)
      alert('移除失敗')
    }
  }

  // 上傳專案圖示 (編輯模式)
  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // 檢查檔案類型
    if (!file.type.startsWith('image/')) {
      alert('只能上傳圖片檔案')
      return
    }

    // 檢查檔案大小 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('圖片檔案不能超過 5MB')
      return
    }

    setUploadingIcon(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: formData,
      })

      const data = await response.json()
      if (data.link) {
        // 使用相對路徑，讓 Nginx 代理處理
        const fullImageUrl = data.link
        setEditForm({ ...editForm, icon: fullImageUrl })
      } else {
        alert('上傳失敗')
      }
    } catch (error) {
      console.error('上傳圖示失敗:', error)
      alert('上傳失敗')
    } finally {
      setUploadingIcon(false)
    }
  }

  // 上傳專案圖示 (建立模式)
  const handleCreateIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // 檢查檔案類型
    if (!file.type.startsWith('image/')) {
      alert('只能上傳圖片檔案')
      return
    }

    // 檢查檔案大小 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('圖片檔案不能超過 5MB')
      return
    }

    setUploadingIcon(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: formData,
      })

      const data = await response.json()
      if (data.link) {
        // 使用相對路徑，讓 Nginx 代理處理
        const fullImageUrl = data.link
        setCreateForm({ ...createForm, icon: fullImageUrl })
      } else {
        alert('上傳失敗')
      }
    } catch (error) {
      console.error('上傳圖示失敗:', error)
      alert('上傳失敗')
    } finally {
      setUploadingIcon(false)
    }
  }

  if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">權限不足</h3>
        <p className="text-gray-500">需要管理員權限才能存取此頁面</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題和控制項 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">專案管理</h1>
          <p className="text-gray-600">管理所有專案、成員和權限</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">顯示封存專案</span>
          </label>
          
          <button
            onClick={openCreateProject}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-4 h-4" />
            <span>建立專案</span>
          </button>
        </div>
      </div>

      {/* 載入狀態 */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        /* 專案列表 */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              className={`card p-4 md:p-6 ${project.archived ? 'bg-gray-50 border-gray-300' : 'hover:shadow-lg'} transition-shadow duration-200`}
            >
              {/* 專案標題和狀態 */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    {project.icon ? (
                      <img src={project.icon} alt="" className="w-8 h-8 rounded" />
                    ) : (
                      <div className="w-8 h-8 bg-primary-100 rounded flex items-center justify-center">
                        <FolderIcon className="w-5 h-5 text-primary-600" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        {project.name}
                        {project.archived && (
                          <span className="ml-2 px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">
                            已封存
                          </span>
                        )}
                        {(project as any).allowPublicView && (
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded flex items-center">
                            <EyeIcon className="w-3 h-3 mr-1" />
                            公開瀏覽
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">{project.key}</p>
                    </div>
                  </div>
                </div>

                {/* 專案操作選單 */}
                <Menu as="div" className="relative">
                  <Menu.Button className="p-2 rounded-md hover:bg-gray-100">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => openEditProject(project)}
                              className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700 w-full text-left`}
                            >
                              <PencilIcon className="w-4 h-4 mr-2" />
                              編輯專案
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => openAddMembers(project)}
                              className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700 w-full text-left`}
                            >
                              <UserPlusIcon className="w-4 h-4 mr-2" />
                              管理成員
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => toggleArchiveProject(project)}
                              className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700 w-full text-left`}
                            >
                              {project.archived ? (
                                <>
                                  <ArchiveBoxXMarkIcon className="w-4 h-4 mr-2" />
                                  取消封存
                                </>
                              ) : (
                                <>
                                  <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                                  封存專案
                                </>
                              )}
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>

              {/* 專案描述 */}
              {project.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {project.description}
                </p>
              )}

              {/* 專案統計 */}
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                    <DocumentTextIcon className="w-4 h-4 mr-1" />
                    {project._count.issues} 議題
                  </span>
                  <span className="flex items-center">
                    <UsersIcon className="w-4 h-4 mr-1" />
                    {project._count.members} 成員
                  </span>
                </div>
              </div>

              {/* 專案成員 */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-900">專案成員</h4>
                <div className="flex flex-wrap gap-2">
                  {project.members.slice(0, 5).map((member) => (
                    <div key={member.id} className="group relative">
                      {member.user.avatar ? (
                        <img
                          src={member.user.avatar}
                          alt={member.user.name}
                          className="w-8 h-8 rounded-full border-2 border-white"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {member.user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      
                      {/* 成員資訊 tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20">
                        {member.user.name}
                        <br />
                        <button
                          onClick={() => removeMember(project.id, member.user.id, member.user.name)}
                          className="text-red-400 hover:text-red-300 pointer-events-auto"
                        >
                          移除
                        </button>
                      </div>
                    </div>
                  ))}
                  {project.members.length > 5 && (
                    <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-600">
                        +{project.members.length - 5}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 編輯專案對話框 */}
      <Transition appear show={!!editingProject} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setEditingProject(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-4 md:p-6 text-left align-middle shadow-xl transition-all mx-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    編輯專案
                  </Dialog.Title>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        專案名稱
                      </label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        專案代碼
                      </label>
                      <input
                        type="text"
                        value={editForm.key}
                        onChange={(e) => {
                          // 只允許英文字母，自動轉大寫
                          const value = e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase()
                          setEditForm({ ...editForm, key: value })
                        }}
                        className="input-field"
                        maxLength={10}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        專案描述
                      </label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={3}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        專案圖示
                      </label>
                      <div className="space-y-3">
                        <input
                          type="url"
                          value={editForm.icon}
                          onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                          className="input-field"
                          placeholder="https://example.com/icon.png"
                        />
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-500">或</span>
                          <label className="relative cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleIconUpload}
                              disabled={uploadingIcon}
                              className="sr-only"
                            />
                            <span className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                              uploadingIcon 
                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                                : 'text-gray-700 bg-white hover:bg-gray-50'
                            }`}>
                              {uploadingIcon ? '上傳中...' : '上傳圖示'}
                            </span>
                          </label>
                        </div>
                        {editForm.icon && (
                          <div className="flex items-center space-x-2">
                            <img src={editForm.icon} alt="預覽" className="w-8 h-8 rounded object-cover" />
                            <span className="text-sm text-green-600">圖示預覽</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.archived}
                          onChange={(e) => setEditForm({ ...editForm, archived: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">封存專案</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.allowPublicView}
                          onChange={(e) => setEditForm({ ...editForm, allowPublicView: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">允許非專案成員瀏覽（唯讀權限）</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-6">啟用後，未加入專案的使用者也能瀏覽此專案的議題，但無法編輯</p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setEditingProject(null)}
                      className="btn-secondary"
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      onClick={handleUpdateProject}
                      className="btn-primary"
                    >
                      更新
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* 成員管理對話框 */}
      <Transition appear show={!!addingMembers} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setAddingMembers(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-4 md:p-6 text-left align-middle shadow-xl transition-all mx-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    管理成員 - {addingMembers?.name}
                  </Dialog.Title>

                  {/* 搜尋欄位 */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      搜尋成員
                    </label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="輸入姓名或電子郵件搜尋..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* 現有成員列表 */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">
                        現有成員 ({addingMembers?.members.length || 0})
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-3 bg-gray-50">
                        {addingMembers?.members
                          .filter(member => {
                            if (!searchTerm) return true
                            const search = searchTerm.toLowerCase()
                            return member.user.name.toLowerCase().includes(search) ||
                                   member.user.email.toLowerCase().includes(search)
                          })
                          .map((member) => (
                            <div key={member.id} className="flex items-center justify-between p-2 bg-white rounded hover:bg-gray-50">
                              <div className="flex items-center space-x-3">
                                {member.user.avatar ? (
                                  <img src={member.user.avatar} alt={member.user.name} className="w-8 h-8 rounded-full" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                                    <span className="text-xs font-medium text-gray-600">
                                      {member.user.name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{member.user.name}</p>
                                  <p className="text-xs text-gray-500">{member.user.email}</p>
                                  {member.user.role && (
                                    <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                                      {member.user.role === 'ADMIN' ? '管理員' : member.user.role === 'SUPER_ADMIN' ? '超級管理員' : '使用者'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => removeMemberFromDialog(member.user.id, member.user.name)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                移除
                              </button>
                            </div>
                          ))}
                        {addingMembers?.members.length === 0 && (
                          <p className="text-gray-500 text-sm text-center py-4">無成員</p>
                        )}
                      </div>
                    </div>

                    {/* 可新增的成員列表 */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">
                        新增成員
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-3">
                        {users
                          .filter(user => !addingMembers?.members.some(m => m.user.id === user.id))
                          .filter(user => {
                            if (!searchTerm) return true
                            const search = searchTerm.toLowerCase()
                            return user.name.toLowerCase().includes(search) ||
                                   user.email.toLowerCase().includes(search)
                          })
                          .map((user) => (
                            <label key={user.id} className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers([...selectedUsers, user.id])
                                  } else {
                                    setSelectedUsers(selectedUsers.filter(id => id !== user.id))
                                  }
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-xs font-medium text-gray-600">
                                    {user.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                <p className="text-xs text-gray-500">{user.email}</p>
                                {user.role && (
                                  <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                                    {user.role === 'ADMIN' ? '管理員' : user.role === 'SUPER_ADMIN' ? '超級管理員' : '使用者'}
                                  </span>
                                )}
                              </div>
                            </label>
                          ))}
                        {users.filter(user => !addingMembers?.members.some(m => m.user.id === user.id)).length === 0 && (
                          <p className="text-gray-500 text-sm text-center py-4">所有使用者都已是專案成員</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setAddingMembers(null)}
                      className="btn-secondary"
                    >
                      關閉
                    </button>
                    <button
                      type="button"
                      onClick={handleAddMembers}
                      disabled={selectedUsers.length === 0}
                      className="btn-primary disabled:opacity-50"
                    >
                      新增選取的成員 ({selectedUsers.length})
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* 建立專案對話框 */}
      <Transition appear show={creatingProject} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setCreatingProject(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-4 md:p-6 text-left align-middle shadow-xl transition-all mx-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    建立專案
                  </Dialog.Title>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        專案名稱 *
                      </label>
                      <input
                        type="text"
                        value={createForm.name}
                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                        className="input-field"
                        placeholder="輸入專案名稱"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        專案代碼 *
                      </label>
                      <input
                        type="text"
                        value={createForm.key}
                        onChange={(e) => {
                          // 只允許英文字母，自動轉大寫
                          const value = e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase()
                          setCreateForm({ ...createForm, key: value })
                        }}
                        className="input-field"
                        maxLength={10}
                        placeholder="專案代碼（如：PROJ）"
                      />
                      <p className="text-xs text-gray-500 mt-1">2-10個英文字母</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        專案描述
                      </label>
                      <textarea
                        value={createForm.description}
                        onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                        rows={3}
                        className="input-field"
                        placeholder="輸入專案描述"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        專案圖示
                      </label>
                      <div className="space-y-3">
                        <input
                          type="url"
                          value={createForm.icon}
                          onChange={(e) => setCreateForm({ ...createForm, icon: e.target.value })}
                          className="input-field"
                          placeholder="https://example.com/icon.png"
                        />
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-500">或</span>
                          <label className="relative cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleCreateIconUpload}
                              disabled={uploadingIcon}
                              className="sr-only"
                            />
                            <span className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                              uploadingIcon 
                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                                : 'text-gray-700 bg-white hover:bg-gray-50'
                            }`}>
                              {uploadingIcon ? '上傳中...' : '上傳圖示'}
                            </span>
                          </label>
                        </div>
                        {createForm.icon && (
                          <div className="flex items-center space-x-2">
                            <img src={createForm.icon} alt="預覽" className="w-8 h-8 rounded object-cover" />
                            <span className="text-sm text-green-600">圖示預覽</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={createForm.allowPublicView}
                          onChange={(e) => setCreateForm({ ...createForm, allowPublicView: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">允許非專案成員瀏覽（唯讀權限）</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-6 mt-1">啟用後，未加入專案的使用者也能瀏覽此專案的議題，但無法編輯</p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setCreatingProject(false)}
                      className="btn-secondary"
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateProject}
                      disabled={!createForm.name || !createForm.key}
                      className="btn-primary disabled:opacity-50"
                    >
                      建立專案
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default ProjectManagement
